import { useCallback, useRef, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
  type KeyboardCoordinateGetter,
  type CollisionDetection,
  type DragStartEvent,
  type DragMoveEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { useNavigate } from "react-router-dom";
import { pinAndAssignPoiToLeg, useTripStore } from "../store/tripStore";
import { useLibraryStore } from "../store/libraryStore";
import { MIN_PLANNER_WIDTH, useUIStore, type TripPane } from "../store/uiStore";
import { useLegStore } from "../store/legStore";
import { useCalendarDndStore } from "../store/calendarDndStore";
import { Button } from "./Button";
import { formatTimeBudget } from "../utils/format";
import {
  addDaysToDate,
  addMinutes,
  moveIntervalToDate,
  zonedTimeOfDay,
} from "../utils/zonedTime";
import {
  moveAccommodationStay,
  moveDateRange,
} from "../utils/calendarRangeResize";
import { requiresCrossZoneGestureConfirmation } from "../utils/calendarGestures";
import {
  SNAP_MINUTES_POINTER,
  SNAP_MINUTES_TOUCH,
  yToZonedTime,
} from "../utils/timeGrid";
import { dndIds } from "../utils/dndIds";
import { Sidebar } from "./Sidebar";
import { BoardDragOverlay } from "./BoardDragOverlay";
import { POIEditorModal } from "./POIEditorModal";
import { AccommodationConfigModal } from "./AccommodationConfigModal";
import { AccommodationPickerModal } from "./AccommodationPickerModal";
import { NoteEditorModal } from "./NoteEditorModal";
import { LegEditorModal } from "./LegEditorModal";
import { ScheduledVisitEditorModal } from "./ScheduledVisitEditorModal";
import { PoiLegPickerModal } from "./PoiLegPickerModal";
import { LockedActionPrompt } from "./LockedActionPrompt";
import { LegErrorNotice } from "./LegErrorNotice";
import { ActivityFeed } from "./ActivityFeed";
import { TripDateRangePicker } from "./TripDateRangePicker";
import { TripTimezoneSettingsModal } from "./TripTimezoneSettingsModal";
import { PlannerPane } from "./PlannerPane";
import { TripMapPane } from "./TripMapPane";
import { PlannerMapResizeHandle } from "./PlannerMapResizeHandle";
import {
  CollapsedPaneRail,
  PANE_RAIL_WIDTH,
  PaneEdgeHandle,
} from "./PaneToggle";
import type {
  BoardDragData,
  CalendarDateDropData,
  CalendarDayDropData,
  LegDropData,
} from "./calendar/dragTypes";
import { useUndoKeyboard } from "../hooks/useUndoKeyboard";
import { useTripSidebarController } from "../hooks/useTripSidebarController";
import { useMediaQuery } from "../hooks/useMediaQuery";
import type { MapBounds } from "../hooks/usePoiFilters";
import { saveStatusToneConfig } from "../theme/colors";
import { useMapPoiSelection } from "../hooks/useMapPoiSelection";
import type { ZonedTimeString } from "@poi-planner/shared";
import { computeDayViews } from "../utils/dayViews";
import {
  clampPlannerFocusDate,
  resolveActiveLeg,
  selectPlannerDayViews,
} from "../utils/plannerDays";
import { buildPlannerMapContext } from "../utils/plannerMapContext";

// Prefer whatever the pointer is inside (day columns, sidebar); fall back to
// rect intersection for keyboard drags
const boardCollisionDetection: CollisionDetection = (args) => {
  const within = pointerWithin(args);
  // Day columns are measured tall enough to reach up behind the all-day area,
  // so a leg bar always competes with the column underneath it. A leg bar is a
  // small, deliberate target: if the pointer is inside one, it wins.
  // The all-day band is sticky inside the week view's scroll container, so the
  // rects dnd-kit measures for it drift by the scroll offset and the pointer
  // never registers as being over a leg. Hit-test the live DOM instead, which
  // cannot go stale, and let a leg beat the day column it overlaps.
  //
  // Only a place from the sidebar can land on a leg. Anything else dragged
  // across the band — a leg bar being moved, which sits under the pointer for
  // the whole gesture, or an all-day note passing over one — must still see
  // the date columns underneath, or its drop is silently swallowed.
  const activeType = (args.active.data.current as BoardDragData | undefined)
    ?.type;
  const pointer = activeType === "sidebar-poi" ? args.pointerCoordinates : null;
  if (pointer) {
    const legId = document
      .elementsFromPoint(pointer.x, pointer.y)
      .map((element) => element.closest<HTMLElement>("[data-leg-drop-id]"))
      .find((element): element is HTMLElement => Boolean(element))
      ?.dataset.legDropId;
    const container = legId
      ? args.droppableContainers.find((entry) => String(entry.id) === legId)
      : undefined;
    if (legId && container) {
      return [{ id: legId, data: { droppableContainer: container, value: 0 } }];
    }
  }
  if (within.length > 0) return within;
  return rectIntersection(args);
};

const boardKeyboardCoordinates: KeyboardCoordinateGetter = (event, args) => {
  const dragData = args.context.active?.data.current as
    | BoardDragData
    | undefined;
  const dropData = args.context.over?.data.current as
    | CalendarDateDropData
    | undefined;
  if (
    (dragData?.type === "accommodation" || dragData?.type === "all-day-note") &&
    dropData?.type === "calendar-date"
  ) {
    const offset =
      event.code === "ArrowLeft"
        ? -1
        : event.code === "ArrowRight"
          ? 1
          : event.code === "ArrowUp"
            ? -7
            : event.code === "ArrowDown"
              ? 7
              : 0;
    if (offset) {
      event.preventDefault();
      const targetId = dndIds.calendarDate(
        addDaysToDate(dropData.date, offset),
      );
      const rect = args.context.droppableRects.get(targetId);
      if (rect) return { x: rect.left, y: rect.top };
    }
  }
  return sortableKeyboardCoordinates(event, args);
};

/**
 * Board chrome shared by every trip view: header, read-only banner, sidebar,
 * DnD context, drag overlay, and the shared modals. Sidebar, Planner and Map
 * are independent panes inside one shared planning workspace.
 *
 * All grid DnD lands here: sidebar→column scheduling, block moves, and
 * sidebar drops for unschedule. Resize and note drag-create use raw pointer
 * events inside the calendar components instead.
 */
export function TripBoardShell() {
  const navigate = useNavigate();
  const {
    trip,
    schedule,
    permission,
    isReadOnly,
    saveStatus,
    schedulePOI,
    unschedulePOI,
    updateScheduledPoi,
    updateNote,
    setSleepAssignments,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useTripStore();
  const updateLeg = useLegStore((state) => state.updateLeg);
  const legs = useLegStore((state) => state.legs);

  const plannerMode = useUIStore((s) => s.plannerMode);
  const plannerFocusDate = useUIStore((s) => s.plannerFocusDate);
  const setPlannerFocusDate = useUIStore((s) => s.setPlannerFocusDate);
  const plannerCalendarStart = useUIStore((s) => s.plannerCalendarStart);
  const activeLegId = useUIStore((s) => s.activeLegId);
  const setActiveLegId = useUIStore((s) => s.setActiveLegId);
  const paneVisibility = useUIStore((s) => s.paneVisibility);
  const setPaneVisible = useUIStore((s) => s.setPaneVisible);
  const plannerWidth = useUIStore((s) => s.plannerWidth);
  const sidebarWidth = useUIStore((s) => s.sidebarWidth);
  const { pois } = useLibraryStore();
  const tags = useLibraryStore((state) => state.tags);
  const allDayViews = useMemo(
    () => (trip ? computeDayViews(trip, schedule) : []),
    [trip, schedule],
  );
  const clampedFocusDate = clampPlannerFocusDate(allDayViews, plannerFocusDate);
  const activeLeg = resolveActiveLeg(legs, activeLegId, clampedFocusDate);
  const plannerDayViews = useMemo(
    () =>
      selectPlannerDayViews(
        plannerMode,
        allDayViews,
        clampedFocusDate,
        activeLeg,
        plannerCalendarStart,
      ),
    [
      plannerMode,
      allDayViews,
      clampedFocusDate,
      activeLeg,
      plannerCalendarStart,
    ],
  );

  useEffect(() => {
    if (clampedFocusDate !== plannerFocusDate) {
      setPlannerFocusDate(clampedFocusDate);
    }
  }, [clampedFocusDate, plannerFocusDate, setPlannerFocusDate]);

  useEffect(() => {
    if ((activeLeg?.id ?? null) !== activeLegId) {
      setActiveLegId(activeLeg?.id ?? null);
    }
  }, [activeLeg?.id, activeLegId, setActiveLegId]);

  const isWideLayout = useMediaQuery("(min-width: 80rem)");
  const [mobileDrawer, setMobileDrawer] = useState<"sidebar" | "map" | null>(
    null,
  );

  // A pane's title button and its collapsed rail replace each other when
  // used, so hand focus to the counterpart rather than dropping it on <body>.
  const paneToggleToFocusRef = useRef<TripPane | null>(null);
  const changePaneVisibility = useCallback(
    (pane: TripPane, visible: boolean) => {
      setPaneVisible(pane, visible);
      // The store refuses to hide the last visible pane
      if (useUIStore.getState().paneVisibility[pane] === visible) {
        paneToggleToFocusRef.current = pane;
      }
    },
    [setPaneVisible],
  );
  useEffect(() => {
    const pane = paneToggleToFocusRef.current;
    if (!pane) return;
    paneToggleToFocusRef.current = null;
    document
      .querySelector<HTMLElement>(`[data-pane-toggle="${pane}"]`)
      ?.focus();
  }, [paneVisibility]);
  const visiblePaneCount = [
    paneVisibility.sidebar,
    paneVisibility.planner,
    paneVisibility.map,
  ].filter(Boolean).length;
  // The last visible pane keeps a plain title: there is nothing to hide it for.
  const collapsePane = (pane: TripPane) =>
    visiblePaneCount > 1 ? () => changePaneVisibility(pane, false) : undefined;
  // Whether a map is actually on screen, not whether the persisted desktop
  // pane preference says so. On the narrow layout the map lives in a drawer
  // that cannot be open at the same time as the sidebar, so filtering the
  // sidebar to the map viewport is never something to offer there — leaving
  // it on would silently narrow the list to an invisible viewport.
  const boundsFilterAvailable = isWideLayout
    ? paneVisibility.map
    : mobileDrawer === "map";
  const [boundsFilterRequested, setBoundsFilterRequested] = useState(false);
  const [mapBounds, setMapBounds] = useState<MapBounds | null>(null);
  const boundsFilterEnabled = boundsFilterAvailable && boundsFilterRequested;
  const sidebarController = useTripSidebarController(pois, schedule, {
    boundsFilterEnabled,
    mapBounds,
  });
  const mapSelection = useMapPoiSelection();
  const plannerMapContext = useMemo(
    () => buildPlannerMapContext(plannerDayViews, schedule, pois),
    [plannerDayViews, schedule, pois],
  );

  useUndoKeyboard();

  const openNoteEditor = useUIStore((s) => s.openNoteEditor);
  const openScheduledVisitEditor = useUIStore(
    (s) => s.openScheduledVisitEditor,
  );
  const [timezoneSettingsOpen, setTimezoneSettingsOpen] = useState(false);
  const mobileDrawerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!mobileDrawer) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    const focusableSelector =
      'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const frame = window.requestAnimationFrame(() => {
      mobileDrawerRef.current
        ?.querySelector<HTMLElement>(focusableSelector)
        ?.focus();
    });
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMobileDrawer(null);
        return;
      }
      if (event.key !== "Tab" || !mobileDrawerRef.current) return;
      const focusable = [
        ...mobileDrawerRef.current.querySelectorAll<HTMLElement>(
          focusableSelector,
        ),
      ];
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("keydown", handleKeyDown);
      previousFocus?.focus();
    };
  }, [mobileDrawer]);

  const setPreview = useCalendarDndStore((s) => s.setPreview);
  const setDateRangePreview = useCalendarDndStore((s) => s.setDateRangePreview);

  // Pointer position drives the drop time for sidebar drags (block drags use
  // the dragged rect itself, which makes grab-offset correction implicit)
  const pointerYRef = useRef(0);
  const snapRef = useRef(SNAP_MINUTES_POINTER);

  useEffect(() => {
    const handler = (e: PointerEvent) => {
      pointerYRef.current = e.clientY;
    };
    window.addEventListener("pointermove", handler, { passive: true });
    return () => window.removeEventListener("pointermove", handler);
  }, []);

  // Disable drag-and-drop sensors in read-only mode. TouchSensor's press
  // delay keeps one-finger scrolling working on the PWA.
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 4 },
      disabled: isReadOnly(),
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 8 },
      disabled: isReadOnly(),
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: boardKeyboardCoordinates,
      disabled: isReadOnly(),
    }),
  );

  /**
   * Snapped zoned start time for the current drag over a day column.
   * Sidebar drags follow the pointer; block drags follow the block's own
   * translated rect (so grabbing mid-body never shifts the start).
   */
  const computeSnappedStart = useCallback(
    (
      event: DragMoveEvent | DragEndEvent,
      dragData: BoardDragData,
      dropData: CalendarDayDropData,
    ): ZonedTimeString | null => {
      const columnEl = document.querySelector(
        `[data-calendar-date="${dropData.date}"]`,
      );
      if (!columnEl) return null;
      const rect = columnEl.getBoundingClientRect();

      const referenceY =
        dragData.type === "sidebar-poi"
          ? pointerYRef.current
          : (event.active.rect.current.translated?.top ?? pointerYRef.current);

      return yToZonedTime(
        referenceY - rect.top,
        dropData.date,
        dropData.zone,
        snapRef.current,
      );
    },
    [],
  );

  const dragSpanMinutes = useCallback(
    (dragData: BoardDragData): number => {
      if (dragData.type === "sidebar-poi") {
        const poi = pois.find((p) => p.id === dragData.poiId);
        return poi?.timeBudget || 60;
      }
      if (dragData.type === "accommodation") {
        return dragData.nightCount * 24 * 60;
      }
      if (dragData.type === "all-day-note" || dragData.type === "leg-range") {
        return dragData.dateCount * 24 * 60;
      }
      return dragData.spanMinutes;
    },
    [pois],
  );

  const dragLabel = useCallback(
    (dragData: BoardDragData): string => {
      if (dragData.type === "sidebar-poi") {
        const poi = pois.find((p) => p.id === dragData.poiId);
        return poi
          ? `${poi.name} · ${formatTimeBudget(poi.timeBudget || 60)}`
          : "";
      }
      return dragData.label;
    },
    [pois],
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const data = event.active.data.current as BoardDragData | undefined;
    if (!data) return;

    // Touch gestures get a coarser snap — 5-min at 0.8px/min is too fine
    const activator = event.activatorEvent;
    const isTouch =
      (typeof TouchEvent !== "undefined" && activator instanceof TouchEvent) ||
      (activator instanceof PointerEvent && activator.pointerType === "touch");
    snapRef.current = isTouch ? SNAP_MINUTES_TOUCH : SNAP_MINUTES_POINTER;
  }, []);

  const handleDragMove = useCallback(
    (event: DragMoveEvent) => {
      const dragData = event.active.data.current as BoardDragData | undefined;
      const dropData = event.over?.data.current as
        | CalendarDayDropData
        | CalendarDateDropData
        | undefined;

      if (
        dragData?.type === "accommodation" &&
        dropData?.type === "calendar-date" &&
        trip
      ) {
        const moved = moveAccommodationStay(
          schedule.sleep,
          dragData.poiId,
          dragData.startDate,
          dragData.endDate,
          dropData.date,
          dragData.grabbedIndex,
          trip.startDate,
          trip.endDate,
        );
        setPreview(null);
        setDateRangePreview({
          startDate: moved?.start ?? dropData.date,
          endDate: moved?.end ?? dropData.date,
          targetDate: dropData.date,
          label: `${dragData.label} · ${dragData.nightCount}n`,
          invalid: !moved,
          conflict: Boolean(moved?.conflicts.length),
        });
        return;
      }

      if (
        dragData?.type === "all-day-note" &&
        dropData?.type === "calendar-date" &&
        trip
      ) {
        const moved = moveDateRange(
          dragData.startDate,
          dragData.endDate,
          dropData.date,
          dragData.grabbedIndex,
          trip.startDate,
          trip.endDate,
        );
        setPreview(null);
        setDateRangePreview({
          startDate: moved?.startDate ?? dropData.date,
          endDate: moved?.endDate ?? dropData.date,
          targetDate: dropData.date,
          label: `${dragData.label} · ${dragData.dateCount}d`,
          invalid: !moved,
          conflict: false,
        });
        return;
      }

      setDateRangePreview(null);
      if (!dragData || dropData?.type !== "calendar-day") {
        setPreview(null);
        return;
      }

      const startTime = computeSnappedStart(event, dragData, dropData);
      if (!startTime) {
        setPreview(null);
        return;
      }

      setPreview({
        date: dropData.date,
        startTime,
        endTime: addMinutes(startTime, dragSpanMinutes(dragData)),
        label: dragLabel(dragData),
        timeLabel: zonedTimeOfDay(startTime),
      });
    },
    [
      computeSnappedStart,
      dragSpanMinutes,
      dragLabel,
      schedule.sleep,
      setDateRangePreview,
      setPreview,
      trip,
    ],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const dragData = event.active.data.current as BoardDragData | undefined;
      const overId = event.over ? String(event.over.id) : null;
      const dropData = event.over?.data.current as
        | CalendarDayDropData
        | CalendarDateDropData
        | LegDropData
        | undefined;

      setPreview(null);
      setDateRangePreview(null);
      // dnd-kit can emit one final move while the drop animation settles.
      // Clear again after that event so date-only previews never remain stuck.
      window.setTimeout(() => setDateRangePreview(null), 0);

      if (!dragData || !overId) return;

      // Dropping a place from the sidebar onto a leg bar assigns it. The
      // place may not be pinned yet, and the membership route rejects
      // unpinned POIs, so this pins first exactly like a calendar drop.
      if (dropData?.type === "leg" && dragData.type === "sidebar-poi") {
        if (!trip) return;
        void pinAndAssignPoiToLeg(trip.id, dropData.legId, dragData.poiId);
        return;
      }

      if (
        dropData?.type === "calendar-date" &&
        dragData.type !== "sidebar-poi"
      ) {
        if (!trip) return;
        if (dragData.type === "accommodation") {
          const moved = moveAccommodationStay(
            schedule.sleep,
            dragData.poiId,
            dragData.startDate,
            dragData.endDate,
            dropData.date,
            dragData.grabbedIndex,
            trip.startDate,
            trip.endDate,
          );
          if (
            !moved ||
            (moved.start === dragData.startDate &&
              moved.end === dragData.endDate)
          ) {
            return;
          }
          if (
            moved.conflicts.length > 0 &&
            !window.confirm(
              `${moved.conflicts.length} night${moved.conflicts.length === 1 ? "" : "s"} already have another accommodation. Replace ${moved.conflicts.join(", ")}?`,
            )
          ) {
            return;
          }
          setSleepAssignments(moved.updates);
          return;
        }
        if (dragData.type === "all-day-note") {
          const moved = moveDateRange(
            dragData.startDate,
            dragData.endDate,
            dropData.date,
            dragData.grabbedIndex,
            trip.startDate,
            trip.endDate,
          );
          if (
            !moved ||
            (moved.startDate === dragData.startDate &&
              moved.endDate === dragData.endDate)
          ) {
            return;
          }
          updateNote(dragData.noteId, {
            allDayStartDate: moved.startDate,
            allDayEndDate: moved.endDate,
          });
          return;
        }
        if (dragData.type === "leg-range") {
          const moved = moveDateRange(
            dragData.startDate,
            dragData.endDate,
            dropData.date,
            dragData.grabbedIndex,
            trip.startDate,
            trip.endDate,
          );
          if (
            !moved ||
            (moved.startDate === dragData.startDate &&
              moved.endDate === dragData.endDate)
          ) {
            return;
          }
          void updateLeg(trip.id, dragData.legId, {
            startDate: moved.startDate,
            endDate: moved.endDate,
          });
          return;
        }
        if (
          dropData.date < trip.startDate ||
          dropData.date > trip.endDate ||
          dropData.date === dragData.originDisplayDate
        ) {
          return;
        }
        const moved = moveIntervalToDate(
          dragData.startTime,
          addMinutes(dragData.startTime, dragData.spanMinutes),
          dropData.date,
          dropData.zone,
          dragData.originDisplayZone,
        );
        if (
          requiresCrossZoneGestureConfirmation(
            dragData.originDisplayZone,
            dropData.zone,
          )
        ) {
          if (dragData.type === "scheduled-poi") {
            openScheduledVisitEditor(dragData.scheduledPoiId, {
              eventType: "scheduledVisit",
              entityId: dragData.scheduledPoiId,
              gesture: "move",
              ...moved,
              originalDisplayZone: dragData.originDisplayZone,
              targetDisplayZone: dropData.zone,
            });
          } else {
            openNoteEditor("edit", dragData.noteId, undefined, {
              eventType: "note",
              entityId: dragData.noteId,
              gesture: "move",
              ...moved,
              originalDisplayZone: dragData.originDisplayZone,
              targetDisplayZone: dropData.zone,
            });
          }
          return;
        }
        if (dragData.type === "scheduled-poi") {
          updateScheduledPoi(dragData.scheduledPoiId, moved);
        } else {
          updateNote(dragData.noteId, moved);
        }
        return;
      }

      // Drop on a day column → schedule / move at the previewed time
      if (dropData?.type === "calendar-day") {
        if (
          dragData.type === "accommodation" ||
          dragData.type === "all-day-note"
        )
          return;
        const startTime = computeSnappedStart(event, dragData, dropData);
        if (!startTime) return;
        const endTime = addMinutes(startTime, dragSpanMinutes(dragData));

        if (dragData.type === "sidebar-poi") {
          schedulePOI(dragData.poiId, startTime);
        } else if (dragData.type === "scheduled-poi") {
          if (
            requiresCrossZoneGestureConfirmation(
              dragData.originDisplayZone,
              dropData.zone,
            )
          ) {
            openScheduledVisitEditor(dragData.scheduledPoiId, {
              eventType: "scheduledVisit",
              entityId: dragData.scheduledPoiId,
              gesture: "move",
              startTime,
              endTime,
              originalDisplayZone: dragData.originDisplayZone,
              targetDisplayZone: dropData.zone,
            });
            return;
          }
          // Store no-ops when the snapped time is unchanged
          updateScheduledPoi(dragData.scheduledPoiId, { startTime, endTime });
        } else if (dragData.type === "note") {
          if (
            requiresCrossZoneGestureConfirmation(
              dragData.originDisplayZone,
              dropData.zone,
            )
          ) {
            openNoteEditor("edit", dragData.noteId, undefined, {
              eventType: "note",
              entityId: dragData.noteId,
              gesture: "move",
              startTime,
              endTime,
              originalDisplayZone: dragData.originDisplayZone,
              targetDisplayZone: dropData.zone,
            });
            return;
          }
          if (startTime !== dragData.startTime) {
            updateNote(dragData.noteId, { startTime, endTime });
          }
        }
        return;
      }

      // Drop on the sidebar → unschedule POIs; notes return to the inbox
      if (overId === dndIds.sidebar) {
        if (dragData.type === "scheduled-poi") {
          unschedulePOI(dragData.scheduledPoiId);
        } else if (dragData.type === "note") {
          updateNote(dragData.noteId, {
            startTime: null,
            endTime: null,
            allDayStartDate: null,
            allDayEndDate: null,
          });
        }
      }
    },
    [
      computeSnappedStart,
      dragSpanMinutes,
      schedulePOI,
      unschedulePOI,
      updateScheduledPoi,
      updateNote,
      updateLeg,
      openNoteEditor,
      openScheduledVisitEditor,
      setSleepAssignments,
      schedule.sleep,
      setDateRangePreview,
      setPreview,
      trip,
    ],
  );

  const handleDragCancel = useCallback(() => {
    setPreview(null);
    setDateRangePreview(null);
    window.setTimeout(() => setDateRangePreview(null), 0);
  }, [setDateRangePreview, setPreview]);

  if (!trip) return null;

  const maxVisiblePlannerWidth = Math.max(
    MIN_PLANNER_WIDTH,
    window.innerWidth -
      (paneVisibility.sidebar ? sidebarWidth : PANE_RAIL_WIDTH) -
      360,
  );
  const visiblePlannerWidth = Math.min(plannerWidth, maxVisiblePlannerWidth);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={boardCollisionDetection}
      autoScroll={{ threshold: { x: 0.15, y: 0.15 } }}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div
        className="flex flex-col h-screen w-full overflow-hidden"
        data-testid="trip-board"
      >
        {/* Top Navigation Bar */}
        <header className="z-10 flex shrink-0 items-center gap-2 whitespace-nowrap border-b border-border bg-white px-3 py-2 md:gap-6 md:px-6 md:py-3">
          {/* Left: Back button + Sidebar toggle + Trip context */}
          <div className="flex flex-1 min-w-0 items-center gap-4">
            {/* Back to trips list */}
            <Button
              onClick={() => navigate("/")}
              variant="icon"
              icon="arrow_back"
              title="Back to trips"
              aria-label="Back to trips"
            />

            <div className="hidden h-6 w-px bg-slate-200 sm:block" />

            {/* Trip name */}
            <h1 className="min-w-0 truncate text-lg font-bold text-slate-800 tracking-tight">
              {trip.name}
            </h1>

            {/* Date range picker */}
            <div className="shrink-0">
              <TripDateRangePicker
                firstDate={trip.startDate}
                lastDate={trip.endDate}
              />
            </div>
          </div>

          {/* Right: Utilities + Brand */}
          <div className="flex flex-1 items-center justify-end gap-1 md:gap-3">
            {!isReadOnly() && (
              <UndoRedoButtons
                canUndo={canUndo()}
                canRedo={canRedo()}
                onUndo={undo}
                onRedo={redo}
              />
            )}
            <ActivityFeed />
            <Button
              onClick={() => setTimezoneSettingsOpen(true)}
              variant="icon"
              icon="language"
              title="Trip timezone settings"
              aria-label="Trip timezone settings"
            />
            <SaveStatusIndicator status={saveStatus} />

            <div className="ml-1 hidden h-6 w-px bg-slate-200 md:block" />

            {/* PlotPlanGo brand (subtle) */}
            <div className="hidden items-center gap-2 text-slate-400 md:flex">
              <svg
                width="14"
                height="14"
                viewBox="0 0 48 48"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M44 11.2727C44 14.0109 39.8386 16.3957 33.69 17.6364C39.8386 18.877 44 21.2618 44 24C44 26.7382 39.8386 29.123 33.69 30.3636C39.8386 31.6043 44 33.9891 44 36.7273C44 40.7439 35.0457 44 24 44C12.9543 44 4 40.7439 4 36.7273C4 33.9891 8.16144 31.6043 14.31 30.3636C8.16144 29.123 4 26.7382 4 24C4 21.2618 8.16144 18.877 14.31 17.6364C8.16144 16.3957 4 14.0109 4 11.2727C4 7.25611 12.9543 4 24 4C35.0457 4 44 7.25611 44 11.2727Z"
                  fill="currentColor"
                />
              </svg>
              <span className="text-xs font-medium tracking-wide">
                PlotPlanGo
              </span>
            </div>
          </div>
        </header>

        {/* Read-Only Mode Banner */}
        {isReadOnly() && (
          <div className="flex items-center justify-center gap-2 px-6 py-2.5 bg-blue-50 border-b border-blue-200 text-blue-900 text-sm">
            <span className="material-symbols-outlined text-base">
              visibility
            </span>
            <span className="font-medium">
              Viewing shared trip - You have read-only access
            </span>
            {permission === "viewer" && (
              <span className="text-xs text-blue-700">
                (Contact the owner for edit access)
              </span>
            )}
          </div>
        )}

        {/* Main Content */}
        <main className="relative flex flex-1 overflow-hidden bg-page">
          {isWideLayout ? (
            <>
              {paneVisibility.sidebar ? (
                <Sidebar
                  controller={sidebarController}
                  onSelectPoi={mapSelection.handleSelectPoi}
                  boundsFilterEnabled={boundsFilterEnabled}
                  onBoundsFilterChange={
                    boundsFilterAvailable ? setBoundsFilterRequested : undefined
                  }
                  onCollapse={collapsePane("sidebar")}
                  fill={!paneVisibility.planner && !paneVisibility.map}
                />
              ) : (
                <CollapsedPaneRail
                  pane="sidebar"
                  onExpand={() => changePaneVisibility("sidebar", true)}
                />
              )}

              {paneVisibility.planner ? (
                <div
                  className="flex min-w-0 shrink-0"
                  style={{
                    width: paneVisibility.map ? visiblePlannerWidth : undefined,
                    flex: paneVisibility.map ? undefined : "1 1 0%",
                  }}
                >
                  <PlannerPane
                    allDayViews={allDayViews}
                    visibleDayViews={plannerDayViews}
                    activeLeg={activeLeg}
                    onShowOnMap={mapSelection.handleSelectPoi}
                    onCollapse={collapsePane("planner")}
                  />
                </div>
              ) : (
                <CollapsedPaneRail
                  pane="planner"
                  onExpand={() => changePaneVisibility("planner", true)}
                  // The Map rail draws the line when the two rails meet
                  divider={paneVisibility.map}
                />
              )}

              {paneVisibility.planner && paneVisibility.map && (
                <PlannerMapResizeHandle
                  width={visiblePlannerWidth}
                  maxWidth={maxVisiblePlannerWidth}
                />
              )}

              {paneVisibility.map ? (
                <TripMapPane
                  plannerContext={plannerMapContext}
                  sidebarPois={sidebarController.filteredPOIs}
                  tags={tags}
                  selectedPoiId={mapSelection.selectedMapPoiId}
                  flyToTrigger={mapSelection.flyToTrigger}
                  onSelectPoi={mapSelection.handleSelectPoi}
                  onBoundsChange={setMapBounds}
                  onCollapse={collapsePane("map")}
                  boundsFilterEnabled={boundsFilterEnabled}
                  fitKey={`${trip.id}:${plannerMode}:${plannerDayViews[0]?.date ?? "empty"}:${plannerDayViews.at(-1)?.date ?? "empty"}`}
                />
              ) : (
                <CollapsedPaneRail
                  pane="map"
                  onExpand={() => changePaneVisibility("map", true)}
                />
              )}
            </>
          ) : (
            <>
              <PlannerPane
                allDayViews={allDayViews}
                visibleDayViews={plannerDayViews}
                activeLeg={activeLeg}
                onShowOnMap={(poiId) => {
                  mapSelection.handleSelectPoi(poiId);
                  setMobileDrawer("map");
                }}
              />
              <PaneEdgeHandle
                pane="sidebar"
                onOpen={() => setMobileDrawer("sidebar")}
              />
              <PaneEdgeHandle
                pane="map"
                onOpen={() => setMobileDrawer("map")}
              />

              {mobileDrawer && (
                <Button
                  variant="unstyled"
                  aria-label="Close open pane"
                  className="absolute inset-0 z-40 bg-primary/20"
                  onClick={() => setMobileDrawer(null)}
                />
              )}
              {mobileDrawer === "sidebar" && (
                <div
                  ref={mobileDrawerRef}
                  className="absolute inset-y-0 left-0 z-50 flex max-w-[90vw] shadow-xl"
                  role="dialog"
                  aria-modal="true"
                  aria-label="Sidebar drawer"
                >
                  <Sidebar
                    controller={sidebarController}
                    onSelectPoi={mapSelection.handleSelectPoi}
                    boundsFilterEnabled={boundsFilterEnabled}
                    onBoundsFilterChange={
                      boundsFilterAvailable
                        ? setBoundsFilterRequested
                        : undefined
                    }
                    onCollapse={() => setMobileDrawer(null)}
                  />
                </div>
              )}
              {mobileDrawer === "map" && (
                <div
                  ref={mobileDrawerRef}
                  className="absolute inset-0 z-50 flex shadow-xl"
                  role="dialog"
                  aria-modal="true"
                  aria-label="Map drawer"
                >
                  <TripMapPane
                    plannerContext={plannerMapContext}
                    sidebarPois={sidebarController.filteredPOIs}
                    tags={tags}
                    selectedPoiId={mapSelection.selectedMapPoiId}
                    flyToTrigger={mapSelection.flyToTrigger}
                    onSelectPoi={mapSelection.handleSelectPoi}
                    onBoundsChange={setMapBounds}
                    onCollapse={() => setMobileDrawer(null)}
                    boundsFilterEnabled={boundsFilterEnabled}
                    fitKey={`${trip.id}:${plannerMode}:${plannerDayViews[0]?.date ?? "empty"}:${plannerDayViews.at(-1)?.date ?? "empty"}`}
                  />
                </div>
              )}
            </>
          )}
        </main>
      </div>

      <BoardDragOverlay pois={pois} />

      <POIEditorModal />
      <AccommodationConfigModal />
      <AccommodationPickerModal />
      <NoteEditorModal />
      <LegEditorModal />
      <ScheduledVisitEditorModal />
      <PoiLegPickerModal />
      <TripTimezoneSettingsModal
        open={timezoneSettingsOpen}
        onClose={() => setTimezoneSettingsOpen(false)}
      />
      <LockedActionPrompt />
      <LegErrorNotice />
    </DndContext>
  );
}

function UndoRedoButtons({
  canUndo,
  canRedo,
  onUndo,
  onRedo,
}: {
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
}) {
  return (
    <div className="flex gap-0.5">
      <Button
        onClick={onUndo}
        disabled={!canUndo}
        variant="icon"
        icon="undo"
        title="Undo (Ctrl+Z)"
        aria-label="Undo"
      />
      <Button
        onClick={onRedo}
        disabled={!canRedo}
        variant="icon"
        icon="redo"
        title="Redo (Ctrl+Shift+Z)"
        aria-label="Redo"
      />
    </div>
  );
}

function SaveStatusIndicator({ status }: { status: string }) {
  const config = saveStatusToneConfig[status] ?? saveStatusToneConfig.saved;

  return (
    <div
      className="flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-md transition-all"
      style={{
        color: config.color,
        background: config.bg,
      }}
    >
      <span
        className={clsx(
          "material-symbols-outlined text-base",
          status === "saving" && "animate-spin",
        )}
      >
        {config.icon}
      </span>
      <span className="hidden md:inline">{config.label}</span>
    </div>
  );
}
